import { ui } from "@/components/ui";
import TrackerButtons from "@/features/trackers/components/TrackerButtons";
import { Route as NewRoute } from "@/routes/_app/diaries/$diaryId/home/new";
import type { StatefulTrackerViewT } from "@/types/database/trackers";
import { useAuth } from "@/utils/auth";
import { AlarmClockCheck, BellRing, CheckCircle2 } from "lucide-react";
import NewStateSwitch from "./NewStateSwitch";

interface Props {
	tracker: StatefulTrackerViewT & { index?: number };
	showDueEntriesInfo?: boolean;
}

const TrackerStateIcon = ({ tracker }: { tracker: StatefulTrackerViewT }) => {
	const isDue = tracker.is_due;
	const isComplete = tracker.tracker_complete;

	return (
		<div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800 px-3 py-1.5 rounded-full">
			{isComplete
				? (
					<>
						<CheckCircle2 className="w-4 h-4 text-green-500" />
						<span className="font-medium text-green-700 dark:text-green-400 text-sm">
							Complete
						</span>
					</>
				)
				: isDue
				? (
					<>
						<BellRing className="w-4 h-4 text-destructive animate-big-wiggle" />
						<span className="font-medium text-destructive text-sm">
							Due
						</span>
					</>
				)
				: (
					<>
						<AlarmClockCheck className="w-4 h-4 text-blue-500" />
						<span className="font-medium text-blue-700 dark:text-blue-400 text-sm">
							Up to date
						</span>
					</>
				)}
		</div>
	);
};

const NewTrackerEntryCard = ({ tracker, showDueEntriesInfo }: Props) => {
	const auth = useAuth();
	const { diaryId } = NewRoute.useParams();

	// Extract values with defaults to handle null/undefined cases
	const isNextEntryLocked = tracker.next_entry?.is_locked ?? false;
	const nextEntryDueAt = tracker.next_entry?.due_at ?? null;
	const nextEntryLabel = tracker.next_entry?.label
		? `Add entry for ${tracker.next_entry?.label}`
		: "Next";
	const dueEntriesCount = tracker.due_entries?.length ?? 0;
	const isPausable = tracker.is_pausable ?? false;

	// Get entries and due entries
	const entries = tracker.entries_view ?? [];
	const dueEntries = tracker.due_entries ?? [];

	return (
		<ui.Card className="flex flex-col justify-center items-center gap-y-4">
			<ui.CardHeader className="flex flex-row justify-between items-start mx-auto p-6 pb-0 w-full">
				<ui.CardTitle className="flex flex-col gap-1 w-full">
					{tracker.index && (
						<span className="font-medium text-slate-600 text-sm">
							Form #{tracker.index}
						</span>
					)}
					{tracker.form.form_name}
				</ui.CardTitle>
				<TrackerStateIcon tracker={tracker} />
			</ui.CardHeader>
			<ui.CardContent className="flex flex-col items-start md:items-start px-6 pt-0 w-full">
				<NewStateSwitch
					endDate={tracker.end_date}
					isComplete={tracker.tracker_complete}
					isNextEntryLocked={isNextEntryLocked}
					nextEntryDueAt={nextEntryDueAt}
					dueEntriesCount={dueEntriesCount}
					nextEntryLabel={nextEntryLabel}
					canMarkComplete={false}
					entries={entries}
					dueEntries={dueEntries}
				/>
			</ui.CardContent>
			<ui.CardFooter className="flex flex-col gap-2 w-full">
				<TrackerButtons
					diaryId={diaryId}
					trackerId={tracker.tracker_id}
					trackerName={tracker.tracker_name ?? ""}
					isNextEntryLocked={isNextEntryLocked}
					isPausable={isPausable}
					endDate={tracker.end_date}
					showNextEntryButton={!isNextEntryLocked && !!nextEntryDueAt}
					showEditButton={auth.isSuperUser}
					nextEntryLabel={nextEntryLabel}
				/>
			</ui.CardFooter>
		</ui.Card>
	);
};

export default NewTrackerEntryCard;
